import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiX, FiCheck, FiArrowRight } from "react-icons/fi";
import CoinIcon from "../components/CoinIcon";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Get from "../services/Get";
import Post from "../services/Post";
import { isApiFailure, unwrapList } from "../services/providerData";
import staking from "../assets/images/staking-1.svg";
import "./SimpleEarn.css";

const earnTabs = ["Advanced Earn", "Monthly Reward", "SCIP", "Super Stake Pro", "Staking"];
const payloadData = (response) => response?.data?.data ?? response?.data ?? response;
const percent = (rate) => `${(Number(rate || 0) * 100).toFixed(2)}%`;
const idempotencyKey = () => window.crypto?.randomUUID?.() ?? `earn-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const balanceForAsset = (balances, symbol) => balances.find((balance) => String(balance.asset?.symbol ?? "").toUpperCase() === String(symbol ?? "").toUpperCase());

function SimpleEarn() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("Advanced Earn");
    const [monthlyPlans, setMonthlyPlans] = useState([]);
    const [myMonthlyPlans, setMyMonthlyPlans] = useState([]);
    const [scipProducts, setScipProducts] = useState([]);
    const [myScipPlans, setMyScipPlans] = useState([]);
    const [superStakeAssets, setSuperStakeAssets] = useState([]);
    const [superStakeOffers, setSuperStakeOffers] = useState([]);
    const [mySuperStakes, setMySuperStakes] = useState([]);
    const [walletBalances, setWalletBalances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [search, setSearch] = useState("");
    const [durationFilter, setDurationFilter] = useState("All Durations");
    const [matchMyAssets, setMatchMyAssets] = useState(false);
    const [expandedRow, setExpandedRow] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [modalTab, setModalTab] = useState("Subscribe");
    const [modalAsset, setModalAsset] = useState("");
    const [amount, setAmount] = useState("");
    const [termMonths, setTermMonths] = useState(24);
    const [dueDay, setDueDay] = useState(15);
    const [preview, setPreview] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [enrollLoading, setEnrollLoading] = useState(false);
    const [modalError, setModalError] = useState("");
    const [successNotice, setSuccessNotice] = useState("");
    const [subscriptionSuccess, setSubscriptionSuccess] = useState(null);

    useEffect(() => {
        let active = true;
        const loadProducts = async () => {
            const results = await Promise.allSettled([
                Get.MonthlyRewardCatalog(),
                Get.MonthlyRewardPlans({ per_page: 50 }),
                Get.ScipProducts(),
                Get.ScipPlans({ per_page: 50 }),
                Get.SuperStakeAssets(),
                Get.SuperStakeOffers(),
                Get.SuperStakes({ per_page: 50 }),
                Get.Wallets({ limit: 50 }),
            ]);
            if (!active) return;

            const responses = results.map((result) => result.status === "fulfilled" ? result.value : null);
            const failed = results.some((result) => result.status === "rejected") || responses.some((response) => response && isApiFailure(response));
            const lists = (response, key) => response && !isApiFailure(response) ? unwrapList(response, key) : [];

            setMonthlyPlans(lists(responses[0], "plans"));
            setMyMonthlyPlans(lists(responses[1], "plans"));
            setScipProducts(lists(responses[2], "products"));
            setMyScipPlans(lists(responses[3], "plans"));
            setSuperStakeAssets(lists(responses[4], "assets"));
            setSuperStakeOffers(lists(responses[5], "offers"));
            setMySuperStakes(lists(responses[6], "stakes"));
            setWalletBalances(Array.isArray(responses[7]?.balances) ? responses[7].balances : []);
            if (failed) setLoadError("Some Earn products could not load. Sign in and refresh to retry.");
            setLoading(false);
        };

        loadProducts();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (!successNotice) return undefined;
        const timeout = window.setTimeout(() => setSuccessNotice(""), 4000);
        return () => window.clearTimeout(timeout);
    }, [successNotice]);

    const refreshWalletBalances = async () => {
        const response = await Get.Wallets({ limit: 50 });
        if (isApiFailure(response) || !Array.isArray(response?.balances)) {
            throw new Error("Could not verify your wallet balance. Refresh and try again.");
        }
        setWalletBalances(response.balances);
        return response.balances;
    };

    const monthlyRows = monthlyPlans.map((plan) => ({
        id: `monthly-${plan.id}`,
        kind: "monthly",
        title: `${plan.plan_code} Monthly Reward`,
        asset: (plan.supported_assets ?? []).join(", "),
        rate: `${percent(plan.monthly_rate)} monthly`,
        duration: `${plan.duration_days} days`,
        locked: true,
        data: plan,
    }));
    const scipRows = scipProducts.map((product) => ({
        id: `scip-${product.asset_symbol}`,
        kind: "scip",
        title: `${product.asset_symbol} SCIP`,
        asset: product.asset_symbol,
        rate: "Fixed monthly quantity",
        duration: `${(product.terms_months ?? []).join(" / ")} months`,
        locked: true,
        data: product,
    }));
    const superStakeRows = superStakeOffers.map((offer) => ({
        id: `stake-${offer.id}`,
        kind: "super-stake",
        title: offer.display_label || `${offer.asset_name || offer.asset} Super Stake Pro`,
        asset: offer.asset || offer.symbol,
        rate: `${percent(offer.reward_rate)} term reward`,
        duration: `${offer.term_days} days`,
        locked: true,
        data: offer,
    }));
    const stakingRows = superStakeAssets.map((asset) => ({
        id: `staking-${asset.id ?? asset.symbol ?? asset.asset_symbol ?? asset.asset ?? asset.coin}`,
        kind: "staking-asset",
        title: `${asset.name ?? asset.asset_name ?? asset.display_name ?? asset.symbol ?? asset.asset_symbol ?? asset.asset ?? asset.coin} Staking`,
        asset: asset.symbol ?? asset.asset_symbol ?? asset.asset ?? asset.coin ?? asset.code,
        rate: "View active offers",
        duration: "Flexible / locked",
        locked: false,
        data: asset,
    }));
    const offerSymbols = new Set(superStakeRows.map((row) => String(row.asset ?? "").toUpperCase()));
    const superStakeAssetRows = stakingRows
        .filter((row) => row.asset && !offerSymbols.has(String(row.asset).toUpperCase()))
        .map((row) => ({ ...row, title: `${row.title.replace(/ Staking$/, "")} Super Stake Pro`, rate: "Eligible asset" }));
    const superStakeProductRows = [...superStakeRows, ...superStakeAssetRows];
    const allRows = [...monthlyRows, ...scipRows, ...superStakeProductRows];
    const categoryRows = {
        "Advanced Earn": allRows,
        "Monthly Reward": monthlyRows,
        SCIP: scipRows,
        "Super Stake Pro": superStakeProductRows,
        Staking: stakingRows,
    };
    const visibleRows = (categoryRows[activeTab] ?? []).filter((row) => {
        const matchesSearch = `${row.title} ${row.asset}`.toLowerCase().includes(search.toLowerCase());
        const matchesDuration = durationFilter === "All Durations" || (durationFilter === "Locked" ? row.locked : !row.locked);
        const heldSymbols = walletBalances.filter((balance) => Number(balance.available ?? balance.balance ?? 0) > 0).map((balance) => String(balance.asset?.symbol ?? "").toUpperCase());
        const rowSymbols = row.kind === "monthly" ? row.data.supported_assets ?? [] : [row.asset];
        const matchesHoldings = !matchMyAssets || rowSymbols.some((symbol) => heldSymbols.includes(String(symbol).toUpperCase()));
        return matchesSearch && matchesDuration && matchesHoldings;
    });

    const openSubscription = (product) => {
        if (product.kind === "staking-asset") {
            setActiveTab("Super Stake Pro");
            setSearch(product.asset);
            return;
        }
        setSelectedProduct(product);
        setModalTab("Subscribe");
        setModalAsset(product.kind === "monthly" ? product.data.supported_assets?.[0] ?? "USDT" : product.asset);
        setAmount(product.kind === "scip" ? product.data.quantity_levels?.[0] ?? "" : product.kind === "super-stake" ? product.data.min_stake ?? "" : "");
        setTermMonths(product.data.terms_months?.[0] ?? 24);
        setDueDay(15);
        setPreview(null);
        setAcceptedTerms(false);
        setModalError("");
    };

    const requestPreview = async (event) => {
        event.preventDefault();
        if (!selectedProduct || !Number(amount) || Number(amount) <= 0) return;
        setPreviewLoading(true);
        setModalError("");
        setPreview(null);
        try {
            let response;
            if (selectedProduct.kind === "monthly") {
                response = await Post.MonthlyRewardPreview({ asset: modalAsset, plan: selectedProduct.data.plan_code, amount: String(amount) });
            } else if (selectedProduct.kind === "scip") {
                response = await Post.ScipPreview({ asset: selectedProduct.data.asset_symbol, quantity: String(amount), term_months: Number(termMonths), due_day: Number(dueDay) });
            } else {
                response = await Post.SuperStakePreview({ offer_id: selectedProduct.data.id, amount: String(amount) });
            }
            setPreview(payloadData(response));
        } catch (error) {
            setModalError(error.message || "Could not preview this subscription.");
        } finally {
            setPreviewLoading(false);
        }
    };

    const confirmSubscription = async () => {
        if (!selectedProduct || !preview || !acceptedTerms) return;
        setEnrollLoading(true);
        setModalError("");
        try {
            const currentBalances = await refreshWalletBalances();
            if (selectedProduct.kind !== "scip") {
                const symbol = selectedProduct.kind === "monthly" ? modalAsset : selectedProduct.asset;
                const available = balanceForAsset(currentBalances, symbol);
                const availableAmount = available?.available ?? available?.balance ?? "0";
                if (Number(availableAmount) < Number(amount)) {
                    throw new Error(`Insufficient ${symbol} balance. Available: ${availableAmount} ${symbol}; required: ${amount} ${symbol}.`);
                }
            }

            const key = idempotencyKey();
            if (selectedProduct.kind === "monthly") {
                await Post.EnrollMonthlyReward({ asset: modalAsset, plan: selectedProduct.data.plan_code, amount: String(amount), acceptedTerms: true }, key);
            } else if (selectedProduct.kind === "scip") {
                await Post.EnrollScip({ asset: selectedProduct.data.asset_symbol, quantity: String(amount), term_months: Number(termMonths), due_day: Number(dueDay), acceptedTerms: true }, key);
            } else {
                await Post.CreateSuperStake({ offer_id: selectedProduct.data.id, amount: String(amount), acceptedTerms: true }, key);
            }

            const subscribedInfo = {
                title: selectedProduct.title,
                asset: selectedProduct.kind === "scip" ? selectedProduct.data.asset_symbol : (selectedProduct.kind === "monthly" ? modalAsset : selectedProduct.asset),
                amount: String(amount),
                date: new Date().toLocaleString(),
                firstDistribution: new Date().toLocaleDateString(),
                kind: selectedProduct.kind,
            };

            setSubscriptionSuccess(subscribedInfo);
            setSuccessNotice(selectedProduct.title);
            setSelectedProduct(null);
            setPreview(null);
            try {
                if (selectedProduct.kind === "monthly") {
                    const positions = await Get.MonthlyRewardPlans({ per_page: 50 });
                    if (!isApiFailure(positions)) setMyMonthlyPlans(unwrapList(positions, "plans"));
                } else if (selectedProduct.kind === "scip") {
                    const plans = await Get.ScipPlans({ per_page: 50 });
                    if (!isApiFailure(plans)) setMyScipPlans(unwrapList(plans, "plans"));
                } else {
                    const stakes = await Get.SuperStakes({ per_page: 50 });
                    if (!isApiFailure(stakes)) setMySuperStakes(unwrapList(stakes, "stakes"));
                }
            } catch {
                setLoadError("Subscription confirmed, but your plan list could not refresh. Reload to see the updated list.");
            }
        } catch (error) {
            setModalError(error.message || "Could not complete this subscription.");
        } finally {
            setEnrollLoading(false);
        }
    };

    const balanceSymbol = selectedProduct?.kind === "scip"
        ? "USDT"
        : selectedProduct?.kind === "monthly" ? modalAsset : selectedProduct?.asset;
    const selectedBalance = balanceForAsset(walletBalances, balanceSymbol);
    const selectedAvailableBalance = selectedBalance?.available ?? selectedBalance?.balance ?? "0";

    const activeCount = myMonthlyPlans.filter((plan) => plan.status === "ACTIVE").length
        + myScipPlans.filter((plan) => plan.status === "ACTIVE").length
        + mySuperStakes.filter((stake) => stake.status === "ACTIVE").length;
    const availableAssets = walletBalances.filter((balance) => Number(balance.available ?? balance.balance ?? 0) > 0).length;
    const termsText = selectedProduct?.kind === "scip"
        ? selectedProduct.data.risk_disclosure
        : selectedProduct?.kind === "super-stake"
            ? `Early withdrawal: ${JSON.stringify(selectedProduct.data.early_withdrawal ?? { enabled: false })}`
            : `Locked for ${selectedProduct?.data.duration_days ?? "the plan"} days. Rewards are calculated according to the selected plan.`;

    return (
        <div className="min-h-screen bg-[#17191f] text-[#eaecef]">
            <Header />
            {successNotice && <div className="earn-success-toast" role="status" aria-live="polite">
                <span className="earn-success-icon" aria-hidden="true">&#10003;</span>
                <div><strong>Subscription confirmed</strong><p>{successNotice}</p></div>
                <button type="button" aria-label="Dismiss success message" onClick={() => setSuccessNotice("")}><FiX /></button>
            </div>}
            <div className="mx-auto w-full max-w-[1120px] px-5 pb-12 pt-4">
                <nav className="mb-7 flex gap-6 overflow-x-auto border-b border-[#303844]" aria-label="Earn products">
                    {earnTabs.map((tab) => <button key={tab} type="button" role="tab" aria-selected={activeTab === tab} onClick={() => { setActiveTab(tab); setExpandedRow(""); }} className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm ${activeTab === tab ? "border-[#f0b90b] text-[#f0b90b]" : "border-transparent text-[#aeb8c6] hover:text-white"}`}>{tab}</button>)}
                </nav>

                <section className="mb-8 grid gap-8 lg:grid-cols-[1fr_320px]">
                    <div>
                        <h1 className="text-3xl font-semibold">Advanced Earn</h1>
                        <p className="mt-2 text-sm text-[#aeb8c6]">Choose an Earn product and review its terms before subscribing.</p>
                        <div className="mt-6 flex flex-wrap gap-10">
                            <div><p className="text-xs text-[#858f9d]">Active plans</p><p className="mt-1 text-lg font-medium">{loading ? "—" : activeCount}</p></div>
                            <div><p className="text-xs text-[#858f9d]">Assets available</p><p className="mt-1 text-lg font-medium">{loading ? "—" : availableAssets}</p></div>
                            <div><p className="text-xs text-[#858f9d]">Monthly Reward plans</p><p className="mt-1 text-lg font-medium">{loading ? "—" : monthlyPlans.length}</p></div>
                        </div>
                    </div>
                    <div className="flex min-h-32 items-center justify-between gap-4 overflow-hidden rounded-md bg-[#202631] px-5 py-4">
                        <div><p className="text-xs text-[#aeb8c6]">Earn with TillionX</p><p className="mt-2 text-xl font-semibold">Plans for every goal</p><p className="mt-1 text-xs text-[#858f9d]">Explore available products and terms</p></div>
                        <img src={staking} alt="" className="h-24 w-24 shrink-0 object-contain" />
                    </div>
                </section>

                <section>
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
                        <div><h2 className="text-xl font-medium">{activeTab === "Advanced Earn" ? "Earn Products" : activeTab}</h2><p className="mt-1 text-xs text-[#858f9d]">Rates, limits, and available plans are loaded from the Earn APIs.</p></div>
                        <div className="flex flex-wrap items-center gap-3">
                            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search coins or plans" aria-label="Search Earn products" className="h-9 w-56 rounded border border-[#303844] bg-[#181a20] px-3 text-sm text-white placeholder:text-[#7f8a99] focus:border-[#f0b90b] focus:outline-none" />
                            <select value={durationFilter} onChange={(event) => setDurationFilter(event.target.value)} aria-label="Filter by duration" className="h-9 rounded border border-[#303844] bg-[#181a20] px-3 text-sm text-[#c1c9d3]">
                                <option>All Durations</option><option>Flexible</option><option>Locked</option>
                            </select>
                            <label className="flex items-center gap-2 text-xs text-[#aeb8c6]"><input type="checkbox" checked={matchMyAssets} onChange={(event) => setMatchMyAssets(event.target.checked)} className="accent-[#f0b90b]" />Match My Assets</label>
                        </div>
                    </div>

                    {loadError && <div role="alert" className="mb-4 rounded border border-[#594d2c] bg-[#25231b] px-4 py-3 text-sm text-[#f0b90b]">{loadError} <Link to="/login" className="underline">Sign in</Link></div>}

                    <div className="overflow-x-auto border-y border-[#303844]">
                        <table className="w-full min-w-[760px] border-collapse text-left text-sm">
                            <thead className="text-xs text-[#858f9d]"><tr><th className="py-3 pr-4 font-normal">Product / Coin</th><th className="py-3 pr-4 font-normal">Rate / Terms</th><th className="py-3 pr-4 font-normal">Duration</th><th className="py-3 pr-4 text-right font-normal">Action</th></tr></thead>
                            <tbody>
                                {loading && <tr><td colSpan="4" className="py-8 text-center text-sm text-[#858f9d]">Loading Earn products…</td></tr>}
                                {!loading && visibleRows.map((row) => <React.Fragment key={row.id}>
                                    <tr className="border-t border-[#303844] hover:bg-[#1d2129]">
                                        <td className="py-4 pr-4"><button type="button" onClick={() => setExpandedRow(expandedRow === row.id ? "" : row.id)} className="flex items-center gap-3 text-left"><CoinIcon className="h-8 w-8 shrink-0 rounded-full object-contain" symbol={String(row.asset ?? "").split(",")[0].trim()} logo={row.data?.logo ?? row.data?.logo_url ?? row.data?.icon ?? row.data?.icon_url ?? row.data?.asset?.logo} name={row.title} /><span><strong className="block font-medium">{row.title}</strong><small className="text-xs text-[#858f9d]">{row.asset}</small></span></button></td>
                                        <td className="py-4 pr-4 text-[#0ecb81]">{row.rate}</td>
                                        <td className="py-4 pr-4 text-[#c1c9d3]">{row.duration}</td>
                                        <td className="py-4 pr-2 text-right">{row.kind === "staking-asset" ? activeTab === "Staking" ? <button type="button" onClick={() => { setSearch(row.asset); setActiveTab("Super Stake Pro"); }} className="rounded bg-[#f0b90b] px-4 py-2 text-xs font-medium text-[#181a20]">View Offers</button> : null : <button type="button" onClick={() => openSubscription(row)} className="rounded bg-[#f0b90b] px-4 py-2 text-xs font-medium text-[#181a20] hover:bg-[#f8d33a]">Subscribe</button>}</td>
                                    </tr>
                                    {expandedRow === row.id && <tr className="border-t border-[#303844] bg-[#1c2028]"><td colSpan="4" className="px-4 py-4 text-xs leading-5 text-[#aeb8c6]">{row.kind === "monthly" ? `Minimum deposit: ${row.data.min_deposit_usdt} USDT equivalent. Supported assets: ${(row.data.supported_assets ?? []).join(", ")}.` : row.kind === "scip" ? `${row.data.risk_disclosure} Terms: ${(row.data.terms_months ?? []).join(", ")} months.` : row.kind === "super-stake" ? `Minimum: ${row.data.min_stake ?? "—"} ${row.asset}. Early withdrawal: ${JSON.stringify(row.data.early_withdrawal ?? { enabled: false })}.` : `Staking enabled for ${row.data.name}. Open Super Stake Pro to view active offers.`}</td></tr>}
                                </React.Fragment>)}
                                {!loading && visibleRows.length === 0 && <tr><td colSpan="4" className="py-10 text-center text-sm text-[#858f9d]">{loadError ? "Sign in to view available Earn products." : "No active products match these filters."}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {/* Subscription Successful Popup (Binance style matching screenshot 2) */}
            {subscriptionSuccess && (
                <div
                    className="earn-modal-backdrop"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setSubscriptionSuccess(null);
                    }}
                >
                    <section
                        className="earn-subscribe-modal p-6 text-center"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="success-modal-title"
                    >
                        <div className="flex justify-end">
                            <button
                                type="button"
                                aria-label="Close dialog"
                                onClick={() => setSubscriptionSuccess(null)}
                                className="text-[#aeb8c6] hover:text-white"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Green Checkmark Circle */}
                        <div className="mx-auto mt-2 flex h-16 w-16 items-center justify-center rounded-full bg-[#0ecb81]/20 text-[#0ecb81]">
                            <FiCheck className="h-8 w-8 stroke-[3]" />
                        </div>

                        <h2 id="success-modal-title" className="mt-4 text-lg font-bold text-white">
                            Subscription Successful
                        </h2>
                        <p className="mt-1 text-2xl font-bold text-white">
                            {subscriptionSuccess.amount} {subscriptionSuccess.asset}
                        </p>

                        <p className="mt-2 text-xs text-[#858f9d] leading-relaxed">
                            Real-Time APR rewards accumulate directly in your Earn Account every minute,
                            while Bonus Tiered APR rewards are distributed to your Spot Account daily.
                        </p>

                        {/* Subscription Timeline */}
                        <div className="my-6 rounded-lg bg-[#181a20] p-4 text-left text-xs space-y-3">
                            <div className="flex items-start justify-between">
                                <span className="flex items-center gap-2 text-[#858f9d]">
                                    <span className="h-2 w-2 rounded-full bg-[#f0b90b]" />
                                    Subscription Date
                                </span>
                                <span className="font-medium text-white">{subscriptionSuccess.date}</span>
                            </div>
                            <div className="flex items-start justify-between">
                                <span className="flex items-center gap-2 text-[#858f9d]">
                                    <span className="h-2 w-2 rounded-full bg-[#0ecb81]" />
                                    First Distribution Date
                                </span>
                                <span className="font-medium text-white">{subscriptionSuccess.firstDistribution} 05:30</span>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="space-y-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setSubscriptionSuccess(null);
                                    navigate("/user/dashboard?tab=earn");
                                }}
                                className="w-full rounded-lg bg-[#f0b90b] py-3 text-xs font-bold text-[#181a20] hover:bg-[#fcd535] transition shadow-md"
                            >
                                Go to Wallet / Assets
                            </button>
                            <button
                                type="button"
                                onClick={() => setSubscriptionSuccess(null)}
                                className="w-full rounded-lg border border-[#303844] py-2.5 text-xs font-semibold text-[#aeb8c6] hover:bg-[#252d38] transition"
                            >
                                Done
                            </button>
                        </div>
                    </section>
                </div>
            )}

            {selectedProduct && <div className="earn-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !enrollLoading) setSelectedProduct(null); }}>
                <section className="earn-subscribe-modal" role="dialog" aria-modal="true" aria-labelledby="earn-modal-title">
                    <header className="flex items-center justify-between border-b border-[#303844] px-5 py-4"><div><h2 id="earn-modal-title" className="text-base font-semibold">{selectedProduct.title}</h2><p className="mt-1 text-xs text-[#858f9d]">{selectedProduct.asset}</p></div><button type="button" aria-label="Close subscription dialog" onClick={() => setSelectedProduct(null)} className="text-[#aeb8c6] hover:text-white"><FiX /></button></header>
                    <div className="flex gap-5 border-b border-[#303844] px-5"><button type="button" onClick={() => setModalTab("Subscribe")} className={`border-b-2 py-3 text-xs ${modalTab === "Subscribe" ? "border-[#f0b90b] text-white" : "border-transparent text-[#858f9d]"}`}>Subscribe</button><button type="button" onClick={() => setModalTab("Product Rules")} className={`border-b-2 py-3 text-xs ${modalTab === "Product Rules" ? "border-[#f0b90b] text-white" : "border-transparent text-[#858f9d]"}`}>Product Rules</button></div>
                    {modalTab === "Subscribe" ? <div className="px-5 py-4">
                        {selectedProduct.kind === "monthly" && <label className="mb-4 block text-xs text-[#aeb8c6]">Asset<select value={modalAsset} onChange={(event) => { setModalAsset(event.target.value); setPreview(null); }} className="mt-2 h-10 w-full rounded border border-[#394453] bg-[#181a20] px-3 text-sm text-white">{(selectedProduct.data.supported_assets ?? []).map((asset) => <option key={asset}>{asset}</option>)}</select></label>}
                        {selectedProduct.kind === "scip" && <><label className="mb-4 block text-xs text-[#aeb8c6]">Term<select value={termMonths} onChange={(event) => { setTermMonths(event.target.value); setPreview(null); }} className="mt-2 h-10 w-full rounded border border-[#394453] bg-[#181a20] px-3 text-sm text-white">{(selectedProduct.data.terms_months ?? []).map((term) => <option key={term} value={term}>{term} months</option>)}</select></label><label className="mb-4 block text-xs text-[#aeb8c6]">Due day<input type="number" min="1" max="28" value={dueDay} onChange={(event) => { setDueDay(event.target.value); setPreview(null); }} className="mt-2 h-10 w-full rounded border border-[#394453] bg-[#181a20] px-3 text-sm text-white" /></label></>}
                        <label className="mb-2 flex items-center justify-between text-xs text-[#aeb8c6]"><span>{selectedProduct.kind === "scip" ? "Monthly quantity" : "Amount"}</span><span className="text-[#f0b90b]">{selectedProduct.kind === "scip" ? selectedProduct.data.asset_symbol : modalAsset}</span></label>
                        {selectedProduct.kind === "scip" && !selectedProduct.data.custom_quantity_enabled ? <select value={amount} onChange={(event) => { setAmount(event.target.value); setPreview(null); }} className="h-10 w-full rounded border border-[#394453] bg-[#181a20] px-3 text-sm text-white">{(selectedProduct.data.quantity_levels ?? []).map((level) => <option key={level} value={level}>{level}</option>)}</select> : <input type="number" min="0" step="any" value={amount} onChange={(event) => { setAmount(event.target.value); setPreview(null); }} placeholder="Enter amount" className="h-10 w-full rounded border border-[#394453] bg-[#181a20] px-3 text-sm text-white placeholder:text-[#737d8c]" />}
                        <p className="mt-2 text-xs text-[#858f9d]">Available {balanceSymbol}: {Number(selectedAvailableBalance).toLocaleString(undefined, { maximumFractionDigits: 8 })}</p>
                        {selectedProduct.kind === "scip" && <p className="mt-1 text-xs text-[#737d8c]">SCIP installments are charged in USDT on their due dates; the wallet balance is checked before each payment is processed.</p>}
                        {selectedProduct.kind === "monthly" && <small className="mt-2 block text-xs text-[#737d8c]">Minimum deposit: {selectedProduct.data.min_deposit_usdt} USDT equivalent</small>}
                        {preview && <div className="mt-4 rounded bg-[#202631] p-3 text-xs"><h3 className="mb-2 font-medium text-white">Preview</h3>{preview.monthly_reward && <p className="mb-1 text-[#aeb8c6]">Estimated monthly reward <strong className="float-right text-[#0ecb81]">{preview.monthly_reward} {modalAsset}</strong></p>}{preview.current_required_usdt && <p className="mb-1 text-[#aeb8c6]">Current required payment <strong className="float-right text-white">{preview.current_required_usdt} USDT</strong></p>}{preview.reward_amount && <p className="mb-1 text-[#aeb8c6]">Estimated term reward <strong className="float-right text-[#0ecb81]">{preview.reward_amount} {preview.asset}</strong></p>}{preview.maturity_amount && <p className="mb-1 text-[#aeb8c6]">Maturity amount <strong className="float-right text-white">{preview.maturity_amount} {preview.asset}</strong></p>}{preview.maturity_at && <p className="text-[#737d8c]">Maturity {new Date(preview.maturity_at).toLocaleDateString()}</p>}{preview.next_due_date && <p className="text-[#737d8c]">Next due {preview.next_due_date}</p>}</div>}
                        {modalError && <p role="alert" className="mt-3 text-xs text-[#f6465d]">{modalError}</p>}
                        {preview && <label className="mt-4 flex items-start gap-2 text-xs leading-5 text-[#aeb8c6]"><input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} className="mt-1 accent-[#f0b90b]" />I have read and agree to the product terms and risk disclosure.</label>}
                        <div className="mt-5 flex justify-end gap-2">{!preview ? <button type="button" onClick={requestPreview} disabled={previewLoading || !Number(amount) || Number(amount) <= 0} className="rounded bg-[#f0b90b] px-5 py-2.5 text-xs font-medium text-[#181a20] disabled:opacity-50">{previewLoading ? "Loading preview..." : "Preview"}</button> : <button type="button" onClick={confirmSubscription} disabled={!acceptedTerms || enrollLoading} className="rounded bg-[#f0b90b] px-5 py-2.5 text-xs font-medium text-[#181a20] disabled:opacity-50">{enrollLoading ? "Confirming..." : "Confirm"}</button>}</div>
                    </div> : <div className="p-5 text-sm leading-6 text-[#aeb8c6]">{termsText || "Review the product terms before subscribing."}<p className="mt-4 text-xs text-[#737d8c]">Digital assets are volatile. Returns and projections are not guaranteed.</p></div>}
                </section>
            </div>}
            <Footer />
        </div>
    );
}

export default SimpleEarn;